import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "../../../../../widgets/SearchPanel";
import { fnGetDataSet, fnGetTableById } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import SnButton from "@shared/components/SnButton";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "@widgets/CommonDialog";

interface TabState {
  page: string;
}

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_INV_REP_001_TEX = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
  });
  const sheetref1 = useRef<any>(null); // excel download 용

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    sheet1: [],
  });

  const [tabPageState, setTabState] = useState<TabState>({
    page: "1",
  });

  const [masCodeSizes, setMasCodeSizes] = useState<any[]>([]);

  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          sheet1: rtn.grid.sheet1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      const size = {
        id: "codel",
        params: { cd_firm: userInfo.cd_firm, cd_clases: ["MAS168"] },
      };
      const data = await fnGetTableById(size);
      setMasCodeSizes(data);
    };
    fetch();
  }, []);

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();

      let params = null;
      switch (tabPageState.page) {
        case "1":
          params = {
            id: "sn_mtl_inv_rep_001_tex_01",
            params: searchDataToUse,
          };
          break;
        case "2":
          params = {
            id: "sn_mtl_inv_rep_001_tex_02",
            params: searchDataToUse,
          };
          break;
        case "3":
          params = {
            id: "sn_mtl_inv_rep_001_tex_03",
            params: searchDataToUse,
          };
          break;
      }

      const data: any = await fnGetDataSet(params);
      switch (tabPageState.page) {
        case "1":
          setDataSources({
            grid1: data[0] || [],
            grid2: [],
            grid3: [],
            sheet1: [],
          });

          const virtual = sortColums(columns.grid1, data[0]);
          getGridCurrent("grid1").changeColumnReorderConfigs(virtual);
          break;
        case "2":
          setDataSources({
            grid1: [],
            grid2: data[0] || [],
            grid3: [],
            sheet1: data[0],
          });
          break;
        case "3":
          setDataSources({
            grid1: [],
            grid2: [],
            grid3: data[0] || [],
            sheet1: data[0],
          });
          break;
      }
    } catch (error: any) {}
  };
  //#endregion

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  //#region onTabChanged
  const onTabChanged = async (index: string) => {
    setTabState({ page: index });
  };
  //#endregion

  const sortColums = (gridColumns: any[], data: any[]) => {
    const normalColumns: any[] = []; // 원래 컬럼
    const sortedColumns: any[] = []; // no_seq로 정렬할 컬럼
    const rowData = data[0];

    gridColumns.forEach((row: any) => {
      if (
        row.cd_column.startsWith("size_") &&
        !row.cd_column.endsWith("_visible")
      ) {
        const fgVisible = rowData[row.cd_column + "_visible"];
        sortedColumns.push({
          cd_column: row.cd_column,
          no_seq: masCodeSizes.find((d: any) => d["cd_flag"] === row.nm_column)
            ?.no_seq_sort,
          fg_visible: fgVisible,
        });
      } else {
        normalColumns.push({
          cd_column: row.cd_column,
          fg_visible: row.fg_visible,
        });
      }
    });

    sortedColumns.sort((a, b) => a.no_seq - b.no_seq);

    return [...normalColumns, ...sortedColumns];
  };

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    const data = searchRef.current.getFormdata();
    if (data.cd_wh === "") {
      await showDialog("alter", "조회조건 창고를 선택하고 조회해 주세요.");
      return;
    }

    const columnKeys = sheetref1.current
      ?.getAllDisplayedColumns()
      .filter((col: any) => {
        const cId = col.getColId();
        return cId !== "fixIndicator";
      })
      .map((c: any) => c.getColId());

    sheetref1.current?.exportDataAsExcel({
      columnKeys,
      skipPinnedBottom: true,
      fileName: "[" + data.nm_wh + `]제품재고-대체출고엑셀양식.xlsx`,
      sheetName: "Sheet1",
    });
  };
  //#endregion

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1">
        <SnTab
          titles={["PIVOT현황", "양품현황", "불량현황"]}
          width={"100%"}
          onTabChanged={onTabChanged}
          header={[
            <SnTabHeader />,
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <SnButton
                    text={"대체출고엑셀양식"}
                    onClick={() => {
                      onSnButtonClick("w");
                    }}
                  />
                </div>,
              ]}
            />,
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <SnButton
                    text={"대체출고엑셀양식"}
                    onClick={() => {
                      onSnButtonClick("b");
                    }}
                  />
                </div>,
              ]}
            />,
          ]}
          body={[
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnGrid
                ref={gridRefs.current["grid1"]} // Grid Name
                columnConfig={grids.grid1.columns} // 컬럼 설정 전달
                rowData={grids.grid1.dataSource} // Datasource
                etcProps={{
                  rowSelection: "multiple",
                  isEditable: false,
                }}
              />
            </div>,
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{
                  rowSelection: "multiple",
                  isEditable: false,
                }}
              />
            </div>,
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnGrid
                ref={gridRefs.current["grid3"]} // Grid Name
                columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                rowData={grids.grid3.dataSource} // Datasource
                etcProps={{
                  rowSelection: "multiple",
                  isEditable: false,
                }}
              />
            </div>,
            <div style={{ display: "none" }}>
              <SnGrid
                ref={sheetref1} // Grid Name
                columnConfig={grids.sheet1.columns} // 컬럼 설정 전달
                rowData={grids.sheet1.dataSource} // Datasource
              />
            </div>,
          ]}
        />
      </div>
    </div>
  );
});

export default SN_MTL_INV_REP_001_TEX;
